import { useEffect, useState } from "react";
import { Clock, XCircle, ShieldAlert, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { BRAND } from "@/constants/branding";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Pill, fmtDateTime } from "@/components/dash";
import VendorApplyForm, { fromApplication, toPayload, checkApplication } from "@/pages/vendor/VendorApplyForm";

const COPY = {
  pending: {
    icon: Clock, tone: "text-amber-600 bg-amber-50", title: "Your application is under review",
    body: "Thanks for applying! Our team is verifying your business and bank details. You'll get a notification here as soon as there's a decision — this usually takes 1–2 working days.",
  },
  rejected: {
    icon: XCircle, tone: "text-red-600 bg-red-50", title: "Your application needs changes",
    body: "We couldn't approve your application yet. Please review the note below, fix the details and resubmit.",
  },
  suspended: {
    icon: ShieldAlert, tone: "text-red-600 bg-red-50", title: "Your seller account is suspended",
    body: "Your products are hidden and you can't take new actions until this is resolved.",
  },
};

export default function VendorGate() {
  const { user, refresh } = useAuth();
  const status = user.vendor_status;
  const meta = COPY[status] || COPY.pending;
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vendor/application").then(({ data }) => {
      setInfo(data);
      if (data.application) setForm(fromApplication(data.application));
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const app = info?.application;
  const canEdit = status === "rejected" || status === "pending";

  const resubmit = async (e) => {
    e.preventDefault();
    const problem = checkApplication(form);
    if (problem) return toast.error(problem);
    setSaving(true);
    try {
      await api.put("/vendor/application", toPayload(form));
      toast.success("Application resubmitted for review.");
      setEditing(false);
      await refresh();
      const { data } = await api.get("/vendor/application");
      setInfo(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const Icon = meta.icon;
  return (
    <DashboardShell title="Seller" subtitle="Seller Application" nav={[{ key: "status", label: "Application status", icon: FileCheck2 }]} active="status" onNavigate={() => {}}>
      <div className="max-w-3xl space-y-5" data-testid="vendor-gate">
        <div className="bg-white rounded-xl border border-border p-6 flex gap-4">
          <div className={`h-12 w-12 rounded-full flex items-center justify-center shrink-0 ${meta.tone}`}><Icon className="h-6 w-6" /></div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-bold text-deep" data-testid="gate-title">{meta.title}</h2>
              <Pill tone={status === "pending" ? "amber" : "red"}>{status}</Pill>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">{meta.body}</p>
            {app?.review_note && status !== "pending" && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="gate-note">
                <p className="font-semibold mb-0.5">Note from Zilkart</p>{app.review_note}
              </div>
            )}
            {status === "suspended" && (
              <p className="text-sm mt-4">Questions? Contact us at <a className="text-primary font-semibold" href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.</p>
            )}
          </div>
        </div>

        {app && (
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h3 className="font-semibold">Submitted details</h3>
              {canEdit && !editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="gate-edit">{status === "rejected" ? "Edit & resubmit" : "Edit application"}</Button>}
            </div>
            {!editing ? (
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  ["Store name", app.store_name], ["Business type", app.business_type?.replace("_", " ")],
                  ["PAN", app.pan_number], ["GSTIN", app.gst_number || "—"],
                  ["Contact", app.contact_phone], ["Address", `${app.address_line1}, ${app.city}, ${app.state} ${app.pincode}`],
                  ["Bank", `${app.bank?.bank_name} · ••••${app.bank?.account_last4}`], ["Submitted", fmtDateTime(app.submitted_at)],
                ].map(([k, v]) => (
                  <div key={k}><dt className="text-muted-foreground text-xs">{k}</dt><dd className="font-medium">{v}</dd></div>
                ))}
              </dl>
            ) : (
              <form onSubmit={resubmit} className="space-y-6">
                <VendorApplyForm form={form} setForm={setForm} />
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="gate-resubmit">{saving ? "Submitting…" : "Submit for review"}</Button>
                </div>
              </form>
            )}
          </div>
        )}

        {app?.history?.length > 0 && (
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="font-semibold mb-3">Timeline</h3>
            <ol className="space-y-3 border-l-2 border-border pl-4">
              {[...app.history].reverse().map((h, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[22px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                  <p className="text-sm font-medium capitalize">{h.status}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(h.at)}{h.note ? ` · ${h.note}` : ""}</p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
